import { createContext, useContext, useState, type ReactNode } from 'react';
import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { projectsApi } from '../../../lib/api/endpoints.js';
import { createEditorStore, type EditorState } from './editorStore.js';

const EditorStoreContext = createContext<StoreApi<EditorState> | null>(null);

/** One store per opened project, so switching projects can never leak state or history. */
export function EditorStoreProvider({ children }: { children: ReactNode }) {
  const [store] = useState(() =>
    createEditorStore(async (id, baseRevision, doc) => {
      const res = await projectsApi.save(id, baseRevision, doc);
      return { revision: res.project.revision };
    }),
  );
  return <EditorStoreContext.Provider value={store}>{children}</EditorStoreContext.Provider>;
}

export function useEditorStoreApi(): StoreApi<EditorState> {
  const store = useContext(EditorStoreContext);
  if (!store) throw new Error('EditorStoreProvider fehlt');
  return store;
}

/** The editor store if there is one (for shared UI pieces that also work outside the editor). */
export function useOptionalEditorStoreApi(): StoreApi<EditorState> | null {
  return useContext(EditorStoreContext);
}

export function useEditorState<T>(selector: (s: EditorState) => T): T {
  return useStore(useEditorStoreApi(), selector);
}

/** What the 3D scene renders: the drag preview while one is active, else the document. */
export const sceneDocument = (s: EditorState) => s.preview ?? s.document;
